/**
 * useFlexOfficeNav — centralised navigation helper for all FlexOffice CTA buttons.
 *
 * Route map:
 *  /flexoffice/pricing   → Pricing section (smooth-scroll to #flexoffice-pricing, or navigate)
 *  /contact              → Contact / Get Started / Talk to Sales / Request Demo
 *  /demo                 → Book a Demo
 *
 * All "contact-like" actions (Contact Us, Talk to Sales, Get Started Free,
 * Get Started with FlexOffice, Request Demo) go to /contact so the sales
 * team receives every lead in one place.
 */

import { useNavigate } from "react-router";

export const ROUTES = {
  contact: "/contact",
  demo: "/contact",          // Demo requests handled by the same contact form
  pricing: "/flexoffice#flexoffice-pricing",
  getStarted: "/contact",
  talkToSales: "/contact",
  requestDemo: "/contact",
};

/**
 * Scrolls smoothly to the pricing section if it exists on the current page,
 * otherwise navigates to the pricing route.
 */
export const scrollToPricing = (navigate) => {
  const el = document.getElementById("flexoffice-pricing");
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    navigate(ROUTES.pricing);
  }
};

/**
 * Hook that returns ready-to-use handlers for every CTA variant.
 *
 * Usage:
 *   const cta = useFlexOfficeCTA();
 *   <button onClick={cta.bookDemo}>Book a Demo</button>
 */
export const useFlexOfficeCTA = () => {
  const navigate = useNavigate();

  return {
    /** "Book a Demo" / "Request Demo" */
    bookDemo: () => navigate(ROUTES.demo),

    /** "View Pricing" / "See Pricing" — scrolls on-page first */
    viewPricing: () => scrollToPricing(navigate),

    /** "Contact Us" */
    contactUs: () => navigate(ROUTES.contact),

    /** "Get Started Free" / "Get Started with FlexOffice" / "Start for Free" */
    getStarted: () => navigate(ROUTES.getStarted),

    /** "Talk to Sales" — enterprise enquiries */
    talkToSales: () => navigate(ROUTES.talkToSales),

    /** Generic contact fallback */
    contact: () => navigate(ROUTES.contact),
  };
};